import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from '@rehub/supabase/auth-storage';
import { getAdminRole as getAdminRoleShared, checkAdminStatus as checkAdminStatusShared } from '@rehub/admin';
import { 
    GeneratedImage, CreditTransaction, PromoCode, PromoRedemption, AdminAction, RedeemPromoResult, AdminUser,
    EventLog, EventType, UserWithStatus, UserTimeline, IPAnalytics, DomainAnalytics,
    UserRiskStatus, ControlStatus, DomainControlStatus, RateLimitCheck,
    Generation, GenerationStatus, CreditLedgerEntry, GenerationsFilters, SalesMetrics
} from '../types';

// Explicitly access environment variables to ensure Vite's static replacement works correctly.
// Dynamic access (e.g. env[key]) often fails in production builds because the bundler cannot tree-shake or inline the values.

const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

const isConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http') && !supabaseUrl.includes('placeholder'));

if (!isConfigured) {
  console.warn(
    'Supabase environment variables missing or invalid. Authentication will be disabled.\n' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment (e.g. .env.local or Vercel Settings).'
  );
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: createCrossSubdomainAuthStorage(),
  },
  global: {
    headers: {},
  },
});

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

// --- CLIENT IP TRACKING ---

let cachedClientIP: string | null = null;
let ipFetchPromise: Promise<string | null> | null = null;

export const fetchClientIP = async (): Promise<string | null> => {
  if (!isConfigured) return null;
  if (cachedClientIP) return cachedClientIP;
  if (ipFetchPromise) return ipFetchPromise;
  ipFetchPromise = (async () => {
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        cachedClientIP = data.ip || null;
        return cachedClientIP;
      }
    } catch (err) {
      console.warn('Failed to fetch client IP:', err);
    }
    return null;
  })();
  return ipFetchPromise;
};

export const getClientIP = (): string | null => cachedClientIP;

export const logEventWithIP = async (
  eventType: EventType,
  userId?: string | null,
  userEmail?: string | null,
  metadata?: Record<string, any>
): Promise<void> => {
  if (!isConfigured) return;
  try {
    await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType,
        userId: userId || null,
        userEmail: userEmail || null,
        metadata: metadata || {},
      }),
    });
  } catch (err) {
    console.warn('Failed to log event with IP:', err);
  }
};

// --- HELPER FUNCTIONS ---

const base64ToBlob = (base64: string): Blob => {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: contentType });
};

export const uploadImageToStorage = async (
  userId: string, 
  imageData: File | Blob | string, 
  type: 'original' | 'processed' | 'sketch' | 'output',
  customId?: string
): Promise<string | null> => {
    if (!isConfigured) return null;
    
    try {
        let blob: Blob;
        let ext = 'jpg';
        
        if (imageData instanceof File) {
             blob = imageData;
             ext = imageData.name.split('.').pop() || 'jpg';
        } else if (typeof imageData === 'string') {
             blob = base64ToBlob(imageData);
             if (imageData.startsWith('data:image/png')) ext = 'png';
             else if (imageData.startsWith('data:image/webp')) ext = 'webp';
        } else {
             blob = imageData;
        }

        // 1. Generate Unique Filename using customId (if provided for linking) or randomUUID
        const uniqueId = customId || crypto.randomUUID();
        
        // 2. Determine Folder Path based on type
        // STRICT STRUCTURE: [userId]/[folder]/[uuid].[ext]
        const folderMap: Record<typeof type, string> = {
            original: 'originals',
            processed: 'results',
            sketch: 'sketches',
            output: 'outputs',
        };
        const folder = folderMap[type];
        const filename = `${userId}/${folder}/${uniqueId}.${ext}`;

        // 3. Upload to 'images' bucket with Upsert: true
        const { data, error } = await supabase.storage
            .from('images')
            .upload(filename, blob, {
                cacheControl: '3600',
                upsert: true
            });

        if (error) {
            console.error('Storage Upload Error:', error);
            throw error;
        }

        // 4. Construct specific Public URL format dynamically
        const { data: { publicUrl } } = supabase.storage
            .from('images')
            .getPublicUrl(filename);
            
        return publicUrl;
    } catch (err) {
        console.error("Failed to upload image:", err);
        throw err;
    }
};

export const getUserImages = async (userId: string): Promise<GeneratedImage[]> => {
    if (!isConfigured) return [];
    
    const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
    if (error) {
        console.error("Fetch Images Error:", error);
        return [];
    }
    return data as GeneratedImage[];
};

export const getUserTransactions = async (userId: string): Promise<CreditTransaction[]> => {
    if (!isConfigured) return [];
    
    const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);
        
    if (error) {
        console.error("Fetch Transactions Error:", error);
        return [];
    }
    return data as CreditTransaction[];
};

export const refundCredit = async (userId: string) => {
    if (!isConfigured) return;
    try {
        await supabase.rpc('refund_credit', { user_id: userId });
        console.log("Credit refunded successfully.");
    } catch (err) {
        console.error("Failed to refund credit:", err);
    }
};

/**
 * Store pending marketing consent before email confirmation
 * This is picked up by the handle_new_user trigger when user confirms
 */
export const storePendingMarketingConsent = async (email: string): Promise<void> => {
    if (!isConfigured) return;
    try {
        await supabase
            .from('pending_marketing_consent')
            .upsert({ email }, { onConflict: 'email' });
    } catch (err) {
        console.error('Failed to store pending marketing consent:', err);
    }
};

/**
 * Process image using server-side edge function
 * Handles generation tracking, credit consumption, and refunds
 */
export const processImageServerSide = async (
    imageBase64: string,
    prompt: string,
    aspectRatio: string = '1:1',
    action: 'declutter' | 'upscale' = 'declutter',
    userId?: string,
    userEmail?: string
): Promise<string> => {
    if (!isConfigured) {
        throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.functions.invoke('process-image', {
        body: {
            imageBase64,
            prompt,
            aspectRatio,
            action,
            userId,
            userEmail
        }
    });

    if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Server processing failed');
    }

    if (!data?.success || !data?.imageBase64) {
        throw new Error(data?.error || 'No image returned from server');
    }

    return data.imageBase64;
};

/**
 * Convert a sketch using a reference image (sketch-to-floor-plan with style example).
 * Routes through the `process-image` edge function so the Gemini key stays
 * server-side. Charges 1 credit if `userId` is provided.
 */
export const convertWithReferenceServerSide = async (
    sketchBase64: string,
    referenceBase64: string,
    prompt: string,
    aspectRatio: string = '1:1',
    userId?: string,
    userEmail?: string
): Promise<string> => {
    if (!isConfigured) {
        throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.functions.invoke('process-image', {
        body: {
            imageBase64: sketchBase64,
            referenceBase64,
            prompt,
            aspectRatio,
            action: 'convert_with_reference',
            userId,
            userEmail,
        },
    });

    if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Server processing failed');
    }

    if (!data?.success || !data?.imageBase64) {
        throw new Error(data?.error || 'No image returned from server');
    }

    return data.imageBase64;
};

/**
 * Extract floor plan vector data (walls + labels JSON) from a sketch using
 * Gemini vision via the `process-image` edge function. This is a free analysis
 * pass — no credit is consumed.
 */
export const extractVectorsServerSide = async (
    imageBase64: string,
    imageWidth: number,
    imageHeight: number,
    architectSystemPrompt: string,
    userId?: string,
    userEmail?: string
): Promise<string> => {
    if (!isConfigured) {
        throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.functions.invoke('process-image', {
        body: {
            imageBase64,
            action: 'extract_vectors',
            imageWidth,
            imageHeight,
            architectSystemPrompt,
            userId,
            userEmail,
        },
    });

    if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Vector extraction failed');
    }

    if (!data?.success || typeof data?.text !== 'string') {
        throw new Error(data?.error || 'No vector data returned from server');
    }

    return data.text;
};

// =========================================
// PROMO CODE FUNCTIONS
// =========================================

export const redeemPromoCode = async (code: string): Promise<RedeemPromoResult> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_AUTHENTICATED' };
    }

    try {
        const { data, error } = await supabase.rpc('redeem_promo_code', {
            p_code: code
        });

        if (error) {
            console.error('Redeem promo error:', error);
            return { success: false, error: 'INVALID_CODE' };
        }

        return data as RedeemPromoResult;
    } catch (err) {
        console.error('Failed to redeem promo code:', err);
        return { success: false, error: 'INVALID_CODE' };
    }
};

export const getPromoCodes = async (): Promise<PromoCode[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) {
        console.error('Fetch promo codes error:', error);
        return [];
    }

    return data as PromoCode[];
};

export const getPromoRedemptions = async (): Promise<PromoRedemption[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('promo_redemptions')
        .select('*')
        .order('redeemed_at', { ascending: false })
        .limit(100);

    if (error) {
        console.error('Fetch promo redemptions error:', error);
        return [];
    }

    return data as PromoRedemption[];
};

// =========================================
// ADMIN FUNCTIONS (shared @rehub/admin; editor RPCs below are sketchplan-specific)
// =========================================

export const getAdminRole = async (userId: string): Promise<AdminUser['role'] | null> => {
    if (!isConfigured) return null;
    return getAdminRoleShared(supabase, userId);
};

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    return checkAdminStatusShared(supabase, userId);
};

// Editor management (superadmin only)
export interface EditorRow {
  user_id: string;
  email: string | null;
}

export interface DefaultEditorRow {
  user_id: string;
  position: number;
  email: string | null;
}

export const getEditors = async (): Promise<EditorRow[]> => {
  if (!isConfigured) return [];
  const { data, error } = await supabase.rpc('admin_get_editors');
  if (error) {
    console.error('getEditors error:', error);
    return [];
  }
  return (data ?? []) as EditorRow[];
};

export const getDefaultEditors = async (): Promise<DefaultEditorRow[]> => {
  if (!isConfigured) return [];
  const { data, error } = await supabase.rpc('admin_get_default_editors');
  if (error) {
    console.error('getDefaultEditors error:', error);
    return [];
  }
  return (data ?? []) as DefaultEditorRow[];
};

export const addEditor = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { error } = await supabase.rpc('admin_add_editor', { p_user_id: userId });
  if (error) return { success: false, error: error.message };
  return { success: true };
};

export const addEditorByEmail = async (
  email: string
): Promise<{ success: boolean; error?: string; user_id?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { data, error } = await supabase.rpc('admin_add_editor_by_email', {
    p_email: email.trim(),
  });
  if (error) return { success: false, error: error.message };
  const row = Array.isArray(data) ? data[0] : data;
  return { success: true, user_id: row?.user_id };
};

export const removeEditor = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { error } = await supabase.rpc('admin_remove_editor', { p_user_id: userId });
  if (error) return { success: false, error: error.message };
  return { success: true };
};

export const setDefaultEditors = async (userIds: string[]): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { error } = await supabase.rpc('admin_set_default_editors', { p_user_ids: userIds });
  if (error) return { success: false, error: error.message };
  return { success: true };
};

async function callAdminCreateEditor(
  payload: Record<string, unknown>
): Promise<{ success: boolean; error?: string; user_id?: string; password?: string; existing?: boolean; action_link?: string }> {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { success: false, error: 'Not signed in' };

  const url = `${validUrl}/functions/v1/admin-create-editor`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: validKey,
    },
    body: JSON.stringify(payload),
  });

  let body: { success?: boolean; error?: string; user_id?: string; password?: string; existing?: boolean; action_link?: string } = {};
  try {
    body = await res.json();
  } catch {
    const fallback = res.status === 404
      ? 'Editor function not deployed. Run: supabase functions deploy admin-create-editor'
      : res.statusText || 'Invalid response from server';
    return { success: false, error: fallback };
  }

  if (!res.ok) {
    const msg = typeof body?.error === 'string' ? body.error : res.status === 404
      ? 'Editor function not deployed. Run: supabase functions deploy admin-create-editor'
      : res.statusText || `Request failed (${res.status})`;
    return { success: false, error: msg };
  }
  if (body?.error) return { success: false, error: body.error };
  if (!body?.success) return { success: false, error: body?.error ?? 'Unknown response' };
  return {
    success: true,
    user_id: body.user_id,
    password: body.password,
    existing: body.existing,
    action_link: body.action_link,
  };
}

/** Create Auth user (or attach an existing one) and add as editor. Superadmin only. */
export const createEditorUser = async (
  email: string,
  password?: string
): Promise<{ success: boolean; error?: string; user_id?: string; password?: string; existing?: boolean }> => {
  return callAdminCreateEditor({
    email: email.trim(),
    password: password || undefined,
    redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
  });
};

/** Generate a magic-link URL the admin can copy and send to an editor. */
export const sendEditorLoginLink = async (
  email: string
): Promise<{ success: boolean; error?: string; action_link?: string }> => {
  return callAdminCreateEditor({
    action: 'send_login_link',
    email: email.trim(),
    redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
  });
};

export const grantBonusCredits = async (
    targetUserId: string,
    amount: number,
    reason: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_grant_bonus_credits', {
            p_target_user_id: targetUserId,
            p_amount: amount,
            p_reason: reason
        });

        if (error) {
            console.error('Grant bonus credits error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to grant bonus credits:', err);
        return { success: false, error: err.message };
    }
};

export const createPromoCode = async (
    code: string,
    credits: number,
    maxUses: number | null = null,
    expiresAt: string | null = null,
    description: string | null = null
): Promise<{ success: boolean; error?: string; promo_id?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_create_promo_code', {
            p_code: code,
            p_credits: credits,
            p_max_uses: maxUses,
            p_expires_at: expiresAt,
            p_description: description
        });

        if (error) {
            console.error('Create promo code error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to create promo code:', err);
        return { success: false, error: err.message };
    }
};

export const updatePromoCode = async (
    promoId: string,
    active?: boolean,
    credits?: number | null,
    maxUses?: number | null,
    expiresAt?: string | null,
    description?: string | null
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_update_promo_code', {
            p_promo_id: promoId,
            p_active: active,
            p_credits: credits,
            p_max_uses: maxUses,
            p_expires_at: expiresAt,
            p_description: description
        });

        if (error) {
            console.error('Update promo code error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update promo code:', err);
        return { success: false, error: err.message };
    }
};

export const getAdminActions = async (): Promise<AdminAction[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('admin_actions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

    if (error) {
        console.error('Fetch admin actions error:', error);
        return [];
    }

    return data as AdminAction[];
};

export const searchUsers = async (searchTerm: string): Promise<any[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('profiles')
        .select('id, email, credits')
        .ilike('email', `%${searchTerm}%`)
        .limit(20);

    if (error) {
        console.error('Search users error:', error);
        return [];
    }

    return data;
};

// =========================================
// BEHAVIOR MONITORING FUNCTIONS
// =========================================

/**
 * Log an event to the event_logs table
 */
export const logEvent = async (
    eventType: EventType,
    userId?: string | null,
    userEmail?: string | null,
    ipAddress?: string | null,
    userAgent?: string | null,
    metadata?: Record<string, any>
): Promise<string | null> => {
    if (!isConfigured) return null;

    try {
        const { data, error } = await supabase.rpc('log_event', {
            p_event_type: eventType,
            p_user_id: userId || null,
            p_user_email: userEmail || null,
            p_ip_address: ipAddress || null,
            p_user_agent: userAgent || null,
            p_metadata: metadata || {}
        });

        if (error) {
            console.error('Log event error:', error);
            return null;
        }

        return data;
    } catch (err) {
        console.error('Failed to log event:', err);
        return null;
    }
};

/**
 * Get activity feed (admin only)
 */
export const getActivityFeed = async (
    limit: number = 100,
    offset: number = 0,
    eventType?: string,
    userId?: string,
    ipAddress?: string
): Promise<{ success: boolean; total?: number; events?: EventLog[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_activity_feed', {
            p_limit: limit,
            p_offset: offset,
            p_event_type: eventType || null,
            p_user_id: userId || null,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Get activity feed error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get activity feed:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get users with their status (admin only)
 */
export const getUsersWithStatus = async (
    limit: number = 50,
    offset: number = 0,
    search?: string,
    statusFilter?: UserRiskStatus
): Promise<{ success: boolean; total?: number; users?: UserWithStatus[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_users_with_status', {
            p_limit: limit,
            p_offset: offset,
            p_search: search || null,
            p_status_filter: statusFilter || null
        });

        if (error) {
            console.error('Get users with status error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get users with status:', err);
        return { success: false, error: err.message };
    }
};

// ---------------------------------------------------------------------------
// Monthly invoiced clients (superadmin only)
// ---------------------------------------------------------------------------

export interface BillingProfile {
    success: boolean;
    user_id?: string;
    email?: string;
    billing_mode?: 'prepaid' | 'monthly_invoice';
    invoice_company?: string | null;
    invoice_email?: string | null;
    custom_plan_rate_pence?: number | null;
    error?: string;
}

export interface InvoiceClient {
    user_id: string;
    email: string;
    invoice_company: string | null;
    invoice_email: string | null;
    custom_plan_rate_pence: number | null;
    month_order_count: number;
    month_amount_pence: number;
    uninvoiced_count: number;
    uninvoiced_amount_pence: number;
}

export interface InvoiceStatementOrder {
    id: string;
    job_number: string | null;
    created_at: string;
    property_address: string;
    estimated_sq_ft: number | null;
    status: string;
    invoice_amount_pence: number | null;
    invoiced_at: string | null;
}

/** Read a single client's billing profile (superadmin only). */
export const getBillingProfile = async (userId: string): Promise<BillingProfile> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
    try {
        const { data, error } = await supabase.rpc('admin_get_billing_profile', { p_user_id: userId });
        if (error) return { success: false, error: error.message };
        return data as BillingProfile;
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

/** Set or clear a client's billing profile (superadmin only). */
export const setBillingProfile = async (params: {
    userId: string;
    billingMode: 'prepaid' | 'monthly_invoice';
    invoiceCompany?: string | null;
    invoiceEmail?: string | null;
    customRatePence?: number | null;
}): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
    try {
        const { data, error } = await supabase.rpc('admin_set_billing_profile', {
            p_user_id: params.userId,
            p_billing_mode: params.billingMode,
            p_invoice_company: params.invoiceCompany ?? null,
            p_invoice_email: params.invoiceEmail ?? null,
            p_custom_rate_pence: params.customRatePence ?? null,
        });
        if (error) return { success: false, error: error.message };
        return data as { success: boolean; error?: string };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

/** List all monthly-invoice clients with current-month + un-invoiced totals (superadmin). */
export const getInvoiceClients = async (): Promise<InvoiceClient[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase.rpc('admin_get_invoice_clients');
    if (error) {
        console.error('getInvoiceClients error:', error);
        return [];
    }
    return (data as InvoiceClient[]) || [];
};

/** Get a client's invoice orders for a given calendar month (superadmin). */
export const getClientInvoiceStatement = async (
    userId: string,
    year: number,
    month: number
): Promise<InvoiceStatementOrder[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase.rpc('admin_get_client_invoice_statement', {
        p_user_id: userId,
        p_year: year,
        p_month: month,
    });
    if (error) {
        console.error('getClientInvoiceStatement error:', error);
        return [];
    }
    return (data as InvoiceStatementOrder[]) || [];
};

/** Mark a set of invoice orders as invoiced (superadmin). */
export const markOrdersInvoiced = async (
    orderIds: string[]
): Promise<{ success: boolean; updated?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
    try {
        const { data, error } = await supabase.rpc('admin_mark_orders_invoiced', { p_order_ids: orderIds });
        if (error) return { success: false, error: error.message };
        return data as { success: boolean; updated?: number; error?: string };
    } catch (err: any) {
        return { success: false, error: err.message };
    }
};

/**
 * Get user timeline (admin only)
 */
export const getUserTimeline = async (userId: string): Promise<{ success: boolean; user?: UserTimeline['user']; events?: EventLog[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_timeline', {
            p_user_id: userId
        });

        if (error) {
            console.error('Get user timeline error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user timeline:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update user status (admin only)
 */
export const updateUserStatus = async (
    userId: string,
    status: UserRiskStatus,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_user_status', {
            p_user_id: userId,
            p_status: status,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update user status error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update user status:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get IP analytics (admin only)
 */
export const getIPAnalytics = async (limit: number = 50, days: number = 30): Promise<{ success: boolean; ips?: IPAnalytics[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_ip_analytics', {
            p_limit: limit,
            p_days: days
        });

        if (error) {
            console.error('Get IP analytics error:', error);
            return { success: false, error: error.message };
        }

        // Database function returns { success, ips } directly
        return data as { success: boolean; ips?: IPAnalytics[]; error?: string };
    } catch (err: any) {
        console.error('Failed to get IP analytics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update IP control (admin only)
 */
export const updateIPControl = async (
    ipAddress: string,
    status: ControlStatus,
    throttleMultiplier: number = 1.0,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_ip_control', {
            p_ip_address: ipAddress,
            p_status: status,
            p_throttle_multiplier: throttleMultiplier,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update IP control error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update IP control:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get domain analytics (admin only)
 */
export const getDomainAnalytics = async (limit: number = 50): Promise<{ success: boolean; domains?: DomainAnalytics[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_domain_analytics', {
            p_limit: limit
        });

        if (error) {
            console.error('Get domain analytics error:', error);
            return { success: false, error: error.message };
        }

        return { success: true, domains: data as DomainAnalytics[] };
    } catch (err: any) {
        console.error('Failed to get domain analytics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update domain control (admin only)
 */
export const updateDomainControl = async (
    domain: string,
    status: DomainControlStatus,
    throttleMultiplier: number = 1.0,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_domain_control', {
            p_domain: domain,
            p_status: status,
            p_throttle_multiplier: throttleMultiplier,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update domain control error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update domain control:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Check rate limits for magic link / login
 */
export const checkRateLimits = async (
    email: string,
    ipAddress?: string
): Promise<RateLimitCheck> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_rate_limits', {
            p_email: email,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Check rate limits error:', error);
            // Fail open - allow if check fails
            return { allowed: true };
        }

        return data as RateLimitCheck;
    } catch (err) {
        console.error('Failed to check rate limits:', err);
        return { allowed: true };
    }
};

/**
 * Check user status for generation permission
 */
export const checkUserGenerationPermission = async (userId: string): Promise<{ allowed: boolean; reason?: string }> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_user_status', {
            p_user_id: userId
        });

        if (error) {
            console.error('Check user status error:', error);
            return { allowed: true };
        }

        return {
            allowed: data.allowed,
            reason: data.reason
        };
    } catch (err) {
        console.error('Failed to check user status:', err);
        return { allowed: true };
    }
};

// =========================================
// GENERATIONS & CREDIT LEDGER FUNCTIONS
// =========================================

/**
 * Create a generation record (for tracking)
 */
export const createGenerationRecord = async (
    userId?: string,
    userEmail?: string,
    metadata?: Record<string, any>
): Promise<string | null> => {
    if (!isConfigured) return null;

    try {
        const { data, error } = await supabase.rpc('create_generation', {
            p_user_id: userId || null,
            p_user_email: userEmail || null,
            p_input_asset_path: null,
            p_ip_address: null,
            p_user_agent: navigator?.userAgent || null,
            p_metadata: metadata || {}
        });

        if (error) {
            console.error('Create generation error:', error);
            return null;
        }

        return data;
    } catch (err) {
        console.error('Failed to create generation:', err);
        return null;
    }
};

/**
 * Update a generation record status
 */
export const updateGenerationRecord = async (
    generationId: string,
    status: 'completed' | 'failed',
    creditsUsed?: number,
    errorMessage?: string,
    outputUrl?: string,
    inputUrl?: string
): Promise<void> => {
    if (!isConfigured) return;

    try {
        await supabase.rpc('update_generation_status', {
            p_generation_id: generationId,
            p_status: status,
            p_output_path: outputUrl || null,
            p_credits_used: creditsUsed || 0,
            p_error_message: errorMessage || null,
            p_metadata: null,
            p_input_path: inputUrl || null
        });
    } catch (err) {
        console.error('Failed to update generation:', err);
    }
};

/**
 * Get paginated list of generations (admin only)
 */
export const getGenerations = async (
    filters: GenerationsFilters = {}
): Promise<{ success: boolean; total?: number; generations?: Generation[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_generations', {
            p_limit: filters.limit || 50,
            p_offset: filters.offset || 0,
            p_user_id: filters.userId || null,
            p_user_email: filters.userEmail || null,
            p_status: filters.status || null,
            p_date_from: filters.dateFrom || null,
            p_date_to: filters.dateTo || null
        });

        if (error) {
            console.error('Get generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user's generations (admin only)
 */
export const getUserGenerations = async (
    userId: string,
    limit: number = 20
): Promise<{ success: boolean; generations?: Generation[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_generations', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user's credit ledger (admin only)
 */
export const getUserCreditLedger = async (
    userId: string,
    limit: number = 50
): Promise<{ success: boolean; balance?: number; entries?: CreditLedgerEntry[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_credit_ledger', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user credit ledger error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user credit ledger:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Adjust user credits (admin only) - uses immutable ledger
 */
export const adjustUserCredits = async (
    userId: string,
    delta: number,
    reason: string
): Promise<{ success: boolean; new_balance?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_adjust_credits', {
            p_user_id: userId,
            p_delta: delta,
            p_reason: reason
        });

        if (error) {
            console.error('Adjust user credits error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to adjust user credits:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get image URL from storage path
 * For public bucket images, returns public URL
 * For private bucket images, would need signed URL (handled server-side)
 */
export const getImageUrl = (storagePath: string | null): string | null => {
    if (!storagePath) return null;
    
    // If it's already a full URL, return as-is
    if (storagePath.startsWith('http')) {
        return storagePath;
    }
    
    // Assume public bucket 'images'
    const { data } = supabase.storage
        .from('images')
        .getPublicUrl(storagePath);
    
    return data.publicUrl;
};

/**
 * Thumbnail URL via Supabase Image Transformations (on-the-fly resize, no pre-created files).
 * Use for grids/lists; keep getImageUrl for download/full-size links.
 * Requires Supabase Pro for /render/image/ transforms.
 */
export const getThumbnailUrl = (
    imageUrl: string | null | undefined,
    options: { width?: number; height?: number; quality?: number; resize?: 'cover' | 'contain' | 'fill' } = {}
): string | null => {
    if (!imageUrl) return null;
    const { width = 400, height = 400, quality = 75, resize = 'cover' } = options;
    const params = new URLSearchParams();
    if (width) params.append('width', width.toString());
    if (height) params.append('height', height.toString());
    if (quality) params.append('quality', quality.toString());
    if (resize) params.append('resize', resize);
    if (imageUrl.includes('/storage/v1/object/')) {
        const transformedUrl = imageUrl.replace('/storage/v1/object/', '/storage/v1/render/image/');
        return `${transformedUrl}?${params.toString()}`;
    }
    const separator = imageUrl.includes('?') ? '&' : '?';
    return `${imageUrl}${separator}${params.toString()}`;
};

/**
 * Get signed URL for private image (admin only)
 * Note: This must be called from an edge function for security
 */
export const getSignedImageUrl = async (
    bucket: string,
    path: string,
    expiresIn: number = 3600
): Promise<{ success: boolean; url?: string; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(path, expiresIn);

        if (error) {
            console.error('Get signed URL error:', error);
            return { success: false, error: error.message };
        }

        return { success: true, url: data.signedUrl };
    } catch (err: any) {
        console.error('Failed to get signed URL:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Admin: Cleanup stuck generations (admin only)
 * Marks generations stuck in 'started' for >threshold minutes as 'failed' and refunds credits
 */
export const cleanupStuckGenerations = async (
    thresholdMinutes: number = 15
): Promise<{ success: boolean; generations_cleaned?: number; credits_refunded?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_cleanup_stuck_generations', {
            p_threshold_minutes: thresholdMinutes
        });

        if (error) {
            console.error('Cleanup stuck generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to cleanup stuck generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get count of stuck generations (for admin display)
 */
export const getStuckGenerationsCount = async (
    thresholdMinutes: number = 15
): Promise<number> => {
    if (!isConfigured) return 0;

    try {
        const thresholdTime = new Date(Date.now() - thresholdMinutes * 60 * 1000).toISOString();
        const { count, error } = await supabase
            .from('generations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'started')
            .lt('created_at', thresholdTime);

        if (error) {
            console.error('Get stuck generations count error:', error);
            return 0;
        }

        return count || 0;
    } catch (err) {
        console.error('Failed to get stuck generations count:', err);
        return 0;
    }
};

/**
 * Admin: Get sales metrics (admin only)
 * Returns profitability metrics for the given date range
 */
export const getSalesMetrics = async (
    dateFrom?: string,
    dateTo?: string
): Promise<{ success: boolean; metrics?: SalesMetrics; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_sales_metrics', {
            p_date_from: dateFrom || null,
            p_date_to: dateTo || null
        });

        if (error) {
            console.error('Get sales metrics error:', error);
            return { success: false, error: error.message };
        }

        if (!data?.success) {
            return { success: false, error: data?.error || 'Unknown error' };
        }

        return { success: true, metrics: data.metrics };
    } catch (err: any) {
        console.error('Failed to get sales metrics:', err);
        return { success: false, error: err.message };
    }
};

export { isConfigured };
